import { useMemo } from 'react';

import {
  InquiryViewType,
  DefaultPSRInquiryFilters,
  DefaultASInquiryFilters,
  ASFilterKeys,
  InquiryFilterKeys,
} from '~/lib/constants/inquiry.constants';
import localStorageConstants from '~/lib/constants/localStorage.constants';
import {
  CareTeamProviderType,
  CareTeamProviderTypeNames,
} from '~/lib/constants/patients.constants';
import { ProviderTypeNames } from '~/lib/constants/user.constants';
import { useComponentPortalContext } from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import useLocalStorage from '~/lib/hooks/useLocalStorage';
import {
  toStatusOptions,
  toDescriptions,
  findCodeByDescription,
  subtractDaysAgoAndFormatISODate,
  getInquiryDateMin,
} from '~/lib/util/inquiry.util';
import { useGetDiagnosisCodesSearchQuery } from '~/store/services/clinicalData';
import {
  useGetNoteCommunicationMethodsEnumQuery,
  useGetProviderTimezonesEnumQuery,
  useGetPayorsEnumQuery,
} from '~/store/services/enumerations';
import {
  useGetMedicalReviewStatusesQuery,
  useGetMedicalReviewTypesQuery,
  useGetMedicalReviewNotClearedReasonsQuery,
  useGetCoverageReviewStatusesQuery,
  useGetInquiryViewsSchemaQuery,
  useUpdateInquiryMedicalReviewStatusMutation,
  useUpdateInquiryMedicalReviewMutation,
  useAddInquiryAssignmentMutation,
  useDeleteInquiryAssignmentMutation,
  useUpdateInquiryInsuranceReviewStatusMutation,
  useUpdateTargetAdmissionMutation,
  useUpdateInquiryNextContactDateMutation,
  useUpdateNextStepsMutation,
  useGetInquiriesViewsQuery,
} from '~/store/services/inquiry';
import { useCreateNoteMutation } from '~/store/services/notes/notes';
import { mapNoteCommunicationMethodsToDropdownOptions } from '../InquiryDetail/viewUtil';
import {
  TagColor,
  ChakraVStack,
  TableFilter,
  RowObject,
  Text,
  TableColumn,
  TableSortModeValue,
  useToastMessage,
} from '@equip.health/ui';
import {
  DiagnosisOptions,
  MedicalReviewState,
} from '@equip.health/ui/lib/types/src/maud/modals/MedicalReview/MedicalReviewModal';
import { AnalyticsEvent } from '~/analytics';
import { createQuickNoteRequest } from '~/components/common/inquiry/drawer/util';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import { InquiryTableRow } from './InquiryTableRow';
import {
  InquiryTableColumnSortingKeys,
  getOrderByDirection,
  InquiryTableColumn,
  getQuickAssignTrackingEvent,
  getQuickUnassignTrackingEvent,
  InquiryTableColumnKeyMap,
} from './utils';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useDatadog } from '~/lib/hooks/useDatadog';
import { useTreatmentPrograms } from '~/lib/hooks/useTreatmentPrograms';
import { useFlags } from 'launchdarkly-react-client-sdk';

export const useInquiryTableData = (
  viewCode: InquiryViewType,
  sortKey: string,
  sortMode: TableSortModeValue,
  pageNumber: number,
  pageSize: number,
) => {
  const { currentUser } = useGlobalContext();
  const { inquiryDetailDrawer } = useComponentPortalContext();

  const {
    inquiryListViewFilterByTreatmentProgram:
      isInquiryListViewFilterByTreatmentProgramEnabled,
  } = useFlags<LDFlags>();
  const { trackEvent } = useAnalytics();

  const { showErrorToast } = useErrorToast();
  const { createToast } = useToastMessage();
  const { logError } = useDatadog();
  const { data: viewColumns, isLoading: isColumnsLoading } =
    useGetInquiryViewsSchemaQuery(viewCode);
  const { data: medicalStatusEnums } = useGetMedicalReviewStatusesQuery();
  const { data: medicalReviewTypeEnums } = useGetMedicalReviewTypesQuery();
  const { data: medicalNotClearedReasonEnums } =
    useGetMedicalReviewNotClearedReasonsQuery();
  const { data: diagnosisCodes } = useGetDiagnosisCodesSearchQuery({
    isAdmissions: true,
  });
  const { data: coverageStatusEnums } = useGetCoverageReviewStatusesQuery();
  const { data: noteCommunicationMethods } =
    useGetNoteCommunicationMethodsEnumQuery();
  const { data: payors } = useGetPayorsEnumQuery();
  const { data: timezoneEnums } = useGetProviderTimezonesEnumQuery();
  const { newEpisodePrograms: treatmentProgramOptions } =
    useTreatmentPrograms();

  const [updateMedicalReviewStatus] =
    useUpdateInquiryMedicalReviewStatusMutation();
  const [updateMedicalReview] = useUpdateInquiryMedicalReviewMutation();
  const [addAssignment] = useAddInquiryAssignmentMutation();
  const [deleteAssignment] = useDeleteInquiryAssignmentMutation();
  const [updateInsuranceReview] =
    useUpdateInquiryInsuranceReviewStatusMutation();
  const [updateTargetAdmissionMutation] = useUpdateTargetAdmissionMutation();
  const [updateInquiryNextContactDate] =
    useUpdateInquiryNextContactDateMutation();
  const [updateInquiryNextSteps] = useUpdateNextStepsMutation();
  const [createNote] = useCreateNoteMutation();

  const timezones = useMemo<string[]>(
    () => (timezoneEnums || []).map((tz) => tz.label),
    [timezoneEnums],
  );

  const communicationOptions = useMemo(() => {
    return mapNoteCommunicationMethodsToDropdownOptions(
      noteCommunicationMethods,
    );
  }, [noteCommunicationMethods]);

  const coverageStatusOptions = useMemo<
    { id: string; color: TagColor; label: string; value: string }[]
  >(
    () =>
      toStatusOptions(coverageStatusEnums).map((option, index) => ({
        ...option,
        color: option.color,
        id: `status-option-${index}`,
        label: option.value,
      })),
    [coverageStatusEnums],
  );

  const medicalStatusOptions = useMemo<CoverageStatusOptionsProps[]>(
    () =>
      toStatusOptions(medicalStatusEnums).map((option, index) => ({
        ...option,
        color: option.color,
        id: `status-option-${index}`,
        label: option.value,
      })),
    [medicalStatusEnums],
  );

  const medicalNotClearedReasonOptions = useMemo(
    () => toDescriptions(medicalNotClearedReasonEnums),
    [medicalNotClearedReasonEnums],
  );
  const medicalTypeOptions = useMemo(
    () => toDescriptions(medicalReviewTypeEnums),
    [medicalReviewTypeEnums],
  );

  // MedicalReviewModal was written to support asynchronous loading of pages of diagnoses. The reality is that we are
  // only using those with `isAdmissions==true` which is a very short list.
  const diagnosisOptions = useMemo<DiagnosisOptions>(
    () => ({
      inputValue: '',
      isLoading: false,
      onLoadMore: () => {},
      onSearch: () => {},
      options: (diagnosisCodes ?? []).map((diagnosis) => {
        return {
          label: (
            <ChakraVStack alignItems="start" key={diagnosis.code} spacing="2px">
              <Text
                color="neutral.primary"
                id={`${diagnosis.code}__diagnosis-description`}
              >
                {diagnosis.shortDescription}
              </Text>
              <Text
                color="neutral.secondary"
                id={`${diagnosis.code}__diagnosis-code`}
                variant="caption"
              >
                {diagnosis.code}
              </Text>
            </ChakraVStack>
          ),
          searchData: diagnosis.shortDescription.toLowerCase(),
          value: diagnosis.shortDescription,
        };
      }),
    }),
    [diagnosisCodes],
  );

  const { columns } = useMemo<{
    columns: TableColumn<InquiryTableColumn>[];
  }>(() => {
    if (isColumnsLoading) return { columns: [] };

    // Sort and map columns
    const columns =
      viewColumns?.data.columns
        .slice()
        .sort((a, b) => a.columnOrder - b.columnOrder)
        .map((column) => ({
          header: column.name,
          key: InquiryTableColumnKeyMap[column.key] ?? column.key,
          sortingKey: column.sortingKey,
          width: column.width,
        })) ?? [];

    return { columns };
  }, [viewColumns, isColumnsLoading]);

  const isViewCodePSR = viewCode === InquiryViewType.PSR;

  const filterTargetTreatmentProgramFilter = (filters: TableFilter[]) => {
    if (!isInquiryListViewFilterByTreatmentProgramEnabled) {
      return filters.filter(
        (filter) =>
          filter.filterKey !== InquiryFilterKeys.TARGET_TREATMENT_PROGRAM,
      );
    }
    return filters;
  };

  const {
    storedValue: inquiryASTableFilters,
    setValue: setInquiryASTableFilters,
  } = useLocalStorage<TableFilter[]>(
    localStorageConstants.inquiryASFilter,
    DefaultASInquiryFilters,
  );

  const {
    storedValue: inquiryPSRTableFilters,
    setValue: setInquiryPSRTableFilters,
  } = useLocalStorage<TableFilter[]>(
    localStorageConstants.inquiryPSRFilter,
    DefaultPSRInquiryFilters,
  );

  const inquiryTableFilters = useMemo(
    () =>
      filterTargetTreatmentProgramFilter(
        isViewCodePSR ? inquiryPSRTableFilters : inquiryASTableFilters,
      ),
    [
      isInquiryListViewFilterByTreatmentProgramEnabled,
      isViewCodePSR,
      inquiryASTableFilters,
      inquiryPSRTableFilters,
    ],
  );

  const setInquiryTableFilters = isViewCodePSR
    ? setInquiryPSRTableFilters
    : setInquiryASTableFilters;

  const filterBy = useMemo<ApiFilterParams>(() => {
    const filters = inquiryTableFilters.reduce<ApiFilterParams>(
      (acc, filter) => ({
        ...(acc as Record<string, string[]>),
        [filter.filterKey]: filter.filters.map(({ value }) => {
          if (filter.filterKey === ASFilterKeys.INSURANCE_COMPANY) {
            return payors?.find((payor) => payor.label === value).externalId;
          }
          if (
            filter.filterKey.includes(
              InquiryTableColumnSortingKeys.lastContactDate,
            )
          ) {
            return subtractDaysAgoAndFormatISODate(
              Number(value),
              filter.filterKey as
                | InquiryFilterKeys.LAST_CONTACT_DATE_MIN
                | InquiryFilterKeys.LAST_CONTACT_DATE_MAX,
            );
          }
          return value.toString();
        }),
      }),
      {} as ApiFilterParams,
    );
    // Ensure inquiry date min filter has a default value if not provided or invalid
    filters[InquiryFilterKeys.INQUIRY_DATE_MIN] = [
      getInquiryDateMin(filters[InquiryFilterKeys.INQUIRY_DATE_MIN]?.[0])
        .toUTC()
        .toISO(),
    ];

    return filters;
  }, [inquiryTableFilters]);

  const {
    data: getInquiriesResponse,
    isFetching,
    isLoading,
  } = useGetInquiriesViewsQuery({
    filterBy,
    includeAvatar: true,
    orderBy: sortKey,
    orderByDirection: getOrderByDirection(sortMode),
    pageNumber,
    pageSize,
    viewCode,
  });

  const { inquiryId: selectedInquiryId } = inquiryDetailDrawer?.data ?? {};
  const tableRows = getInquiriesResponse?.data;
  const isTableLoading = isLoading || isColumnsLoading;

  const rows: RowObject<InquiryTableColumn>[] = useMemo(() => {
    if (!tableRows) return [];

    const handleChangeTargetAdmissionDate = (
      date: Date,
      inquiryId: string,
      version: string,
    ) => {
      updateTargetAdmissionMutation({
        inquiryId,
        showConflictErrorModal: true,
        targetAdmissionDate: date,
        version,
      })
        .unwrap()
        .catch((error) => {
          showErrorToast(
            GlobalErrorCode.EDIT_INQUIRY_V2,
            error.status,
            error.data?.message,
          );
        });
    };

    const handleUpdateNextContactDate = (
      inquiryId: string,
      date: Date,
      version: string,
    ) => {
      updateInquiryNextContactDate({
        inquiryId,
        nextContactDate: date,
        showConflictErrorModal: true,
        version,
      });
    };

    const handleAddNote = (
      comment: string,
      contactMethod: string,
      inquiryId: string,
      isPatientPrimaryContact: boolean,
      patientId: string,
    ) => {
      const { userId } = currentUser || {};

      const payload = createQuickNoteRequest({
        activityMedium: contactMethod,
        inquiryId,
        isPatientPrimaryContact,
        noteContent: comment,
        patientExternalId: patientId,
        userExternalId: userId,
      });

      createNote(payload);
    };

    const handleUpdateMedicalReviewStatus = (
      inquiryId: string,
      patientId: string,
      status: string,
      version: string,
    ) => {
      updateMedicalReviewStatus({
        description: status,
        inquiryId,
        patientId,
        status: findCodeByDescription(status, medicalStatusEnums),
        version,
      });
    };

    const handleUpdateMedicalReview = (
      inquiryId: string,
      medicalEditState: MedicalReviewState,
      patientId: string,
      version: string,
    ) => {
      return updateMedicalReview({
        description: medicalEditState.status,
        inquiryId,
        notClearedReason: findCodeByDescription(
          medicalEditState.notClearedReason,
          medicalNotClearedReasonEnums,
        ),
        patientId,
        preliminaryDiagnosis: (diagnosisCodes ?? []).find(
          (x) => x.shortDescription === medicalEditState.preliminaryDiagnosis,
        ),
        status: findCodeByDescription(
          medicalEditState.status,
          medicalStatusEnums,
        ),
        type: findCodeByDescription(
          medicalEditState.type,
          medicalReviewTypeEnums,
        ),
        version,
      })
        .unwrap()
        .catch((error) =>
          showErrorToast(
            GlobalErrorCode.UPDATE_INQUIRY_ASSIGNMENTS,
            error.status,
            error.data?.message,
          ),
        );
    };

    const updateNextSteps = (
      inquiryId: string,
      nextSteps: string,
      version: string,
    ) => {
      return updateInquiryNextSteps({
        inquiryId,
        nextSteps,
        shouldInvalidate: false,
        showConflictErrorModal: true,
        version,
      })
        .unwrap()
        .catch((error) => {
          showErrorToast(
            GlobalErrorCode.UPDATE_NEXT_STEPS,
            error.status,
            error.data?.message,
          );
        });
    };

    const {
      firstName,
      lastName,
      providerTypeName: currentUserRole,
      userId,
    } = currentUser || {};
    const track = (eventName: AnalyticsEvent, inquiryId: string): void => {
      trackEvent(eventName, { inquiryId, source: 'Inquiry List' });
    };

    const handleAddAssignment = (
      inquiryId: string,
      providerRole: CareTeamProviderType,
      version: string,
    ) => {
      track(getQuickAssignTrackingEvent(providerRole), inquiryId);
      addAssignment({
        assignment: {
          assignmentId: null,
          externalId: userId,
          firstName,
          lastName,
          providerType: currentUserRole,
        },
        inquiryId,
        providerType: CareTeamProviderTypeNames[providerRole],
        version,
      });
    };

    const isAdmissionsSupervisor =
      currentUser?.providerTypeName === ProviderTypeNames.ADMISSIONS_SUPERVISOR;

    const handleSupervisorAssignment = async (
      provider: GetProviderByIdResponse,
      inquiryId: string,
      providerRole: CareTeamProviderType,
      version: string,
      currentAssignee?: InquiryListItemAssignment,
    ) => {
      const providerType = CareTeamProviderTypeNames[providerRole];
      const roleSlug = String(providerRole).replace(/\s+/g, '-');

      try {
        track(getQuickAssignTrackingEvent(providerRole), inquiryId);
        let currentVersion = version;

        // If replacing an existing assignment, delete it first to get the updated version
        if (currentAssignee) {
          const deleteResult = await deleteAssignment({
            assignmentId: currentAssignee.assignmentId,
            inquiryId,
            providerType,
            version: currentVersion,
          }).unwrap();
          currentVersion = deleteResult.version;
        }

        // Add the new assignment with the current version
        await addAssignment({
          assignment: {
            assignmentId: null,
            externalId: provider.userId,
            firstName: provider.firstName,
            lastName: provider.lastName,
            providerType,
          },
          inquiryId,
          providerType,
          version: currentVersion,
        }).unwrap();

        createToast({
          id: `supervisor-assignment-success-${inquiryId}-${roleSlug}`,
          text: `Successfully assigned to ${provider.firstName} ${provider.lastName}`,
          variant: 'success',
        });
      } catch (error) {
        logError(error as Error, {
          inquiryId,
          providerRole,
          providerId: provider.userId,
          operation: currentAssignee ? 'replace' : 'add',
        });
        createToast({
          id: `supervisor-assignment-error-${inquiryId}-${roleSlug}`,
          text: 'Assignment failed. Please try again.',
          variant: 'critical',
        });
      }
    };
    const handleRemoveAssignment = (
      firstAssignee: InquiryListItemAssignment,
      inquiryId: string,
      providerRole: CareTeamProviderType,
      version: string,
    ) => {
      track(getQuickUnassignTrackingEvent(providerRole), inquiryId);
      deleteAssignment({
        assignmentId: firstAssignee.assignmentId,
        inquiryId,
        providerType: CareTeamProviderTypeNames[providerRole],
        version,
      });
    };
    const updateInsuranceReviewStatus = (
      inquiryId,
      selectedStatus,
      version,
    ) => {
      updateInsuranceReview({
        description: selectedStatus,
        inquiryId,
        status: findCodeByDescription(selectedStatus, coverageStatusEnums),
        version,
      }).catch((error) => console.error(error?.data?.message));
    };

    const inquiryTableRowProps: InquiryTableRowProps = {
      AddNote: handleAddNote,
      communicationOptions,
      coverageStatusOptions,
      currentUser,
      diagnosisOptions,
      handleAddAssignment,
      handleChangeTargetAdmissionDate,
      handleRemoveAssignment,
      ...(isAdmissionsSupervisor && { handleSupervisorAssignment }),
      handleUpdateMedicalReview,
      handleUpdateMedicalReviewStatus,
      inquiry: null,
      isLoading: false,
      medicalNotClearedReasonOptions,
      medicalStatusOptions,
      medicalTypeOptions,
      selectedInquiryId,
      updateInsuranceReviewStatus,
      updateNextContactDate: handleUpdateNextContactDate,
      updateNextSteps,
    };
    return tableRows.map((inquiry) => {
      return InquiryTableRow({
        ...inquiryTableRowProps,
        inquiry: {
          ...inquiry,
          targetTreatmentProgram:
            isInquiryListViewFilterByTreatmentProgramEnabled
              ? inquiry.targetTreatmentProgram
              : undefined,
        },
        isLoading: inquiry.inquiryId === selectedInquiryId && isFetching,
      });
    });
  }, [
    addAssignment,
    communicationOptions,
    coverageStatusEnums,
    coverageStatusOptions,
    createNote,
    currentUser,
    deleteAssignment,
    diagnosisCodes,
    diagnosisOptions,
    isFetching,
    isInquiryListViewFilterByTreatmentProgramEnabled,
    medicalNotClearedReasonEnums,
    medicalNotClearedReasonOptions,
    medicalReviewTypeEnums,
    medicalStatusEnums,
    medicalStatusOptions,
    medicalTypeOptions,
    selectedInquiryId,
    showErrorToast,
    tableRows,
    trackEvent,
    updateInquiryNextContactDate,
    updateInsuranceReview,
    updateMedicalReview,
    updateMedicalReviewStatus,
    updateTargetAdmissionMutation,
    updateInquiryNextSteps,
  ]);

  return {
    columns,
    coverageStatusOptions,
    inquiryTableFilters,
    isTableLoading,
    medicalStatusOptions,
    payors,
    rows,
    selectedInquiryId,
    setInquiryTableFilters,
    timezones,
    totalCount: getInquiriesResponse?.totalCount,
    treatmentProgramOptions,
  };
};
